/**
 * Facebook & Marketing Pixel Utility (Adapter for Analytics Bus)
 * 
 * Delegates all events to the central decoupled AnalyticsBus (@modules/analytics).
 * Preserves 100% backward compatibility for existing pages while guaranteeing
 * that Facebook, TikTok, and GTM are completely sandboxed and modularized.
 */

import { analytics } from '../modules/analytics';

export const trackFacebookEvent = (eventName: string, data?: Record<string, any>): Promise<void> => {
  return analytics.track(eventName, data);
};

export const trackCustomEvent = (eventName: string, data?: Record<string, any>): Promise<void> => {
  const fbPlugin = analytics.getPlugin('facebook');
  if (fbPlugin && typeof window !== 'undefined' && window.fbq) {
    const eventData = {
      ...data,
      event_id: data?.event_id || `${eventName}_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`,
    };
    try {
      window.fbq('trackCustom', eventName, eventData);
    } catch (e) {
      console.error('[Facebook Pixel] trackCustom error:', e);
    }
  }
  return Promise.resolve();
};

/**
 * Predefined event helpers for common e-commerce actions
 */
export const fbEvents = {
  viewContent: (product: {
    id: string | number;
    name: string;
    price: number;
    category?: string;
  }) => analytics.viewContent(product),

  addToCart: (product: {
    id: string | number;
    name: string;
    price: number;
    quantity?: number;
  }) => analytics.addToCart(product),

  addToWishlist: (product: {
    id: string | number;
    name: string;
    price: number;
  }) => analytics.track('AddToWishlist', {
    content_name: product.name,
    content_id: String(product.id),
    content_ids: [String(product.id)],
    content_type: 'product',
    value: product.price,
    currency: 'BDT',
  }),

  initiateCheckout: (items: Array<{
    id: string | number;
    quantity: number;
  }>, totalValue: number) => analytics.initiateCheckout(
    items.map(item => ({ content_id: String(item.id), quantity: item.quantity })),
    totalValue
  ),

  purchase: (order: {
    orderId: string;
    items: Array<{ id: string | number; quantity: number }>;
    totalValue: number;
  }) => analytics.purchase({
    orderId: order.orderId,
    totalAmount: order.totalValue,
    items: order.items.map(i => ({ id: i.id, quantity: i.quantity })),
    currency: 'BDT',
  }),

  search: (searchQuery: string) => analytics.track('Search', {
    search_string: searchQuery,
    content_type: 'product',
  }),
};
